import { Hammer, MapPin, Calendar, Star, Building2, Award, ArrowRight, Phone } from "lucide-react";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F0EBE0";
const INK = "#1A1F2E";
const INK_SOFT = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const STATS = [{
  Icon: Hammer,
  value: "1,247",
  label: "Doors installed",
  detail: "Since 2009. Counted, not estimated.",
  featured: true
}, {
  Icon: MapPin,
  value: "217+",
  label: "Sydney suburbs",
  detail: "Bondi to Penrith, Manly to Cronulla."
}, {
  Icon: Calendar,
  value: "17",
  label: "Years operating",
  detail: "Family-run from Bass Hill, NSW."
}, {
  Icon: Star,
  value: "5.0",
  label: "Google rating",
  detail: "Over 200 reviews. Zero below 4★."
}, {
  Icon: Building2,
  value: "8+",
  label: "Brand partners",
  detail: "B&D · Steel-Line · Centurion · Merlin"
}, {
  Icon: Award,
  value: "1 Yr",
  label: "Labour warranty",
  detail: "On every install. Not a sales line."
}];
export default function GalleryStats() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Paper grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
    }} />

      {/* Corner ornaments */}
      <div aria-hidden className="absolute top-10 right-10 pointer-events-none" style={{
      opacity: 0.55,
      transform: "rotate(90deg)"
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>
      <div aria-hidden className="absolute bottom-10 left-10 pointer-events-none" style={{
      opacity: 0.55,
      transform: "rotate(270deg)"
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <div aria-hidden className="h-px w-12" style={{
            backgroundColor: GOLD
          }} />
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: GOLD_DARK
          }}>
              08 · By the Numbers
            </span>
          </div>

          <h2 style={{
          fontFamily: FONT_HEAD,
          fontWeight: 800,
          fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          color: NAVY,
          marginBottom: "20px"
        }}>
            The maths
            <br />
            <span style={{
            color: GOLD_DARK
          }}>
              behind the gallery
            </span>
            .
          </h2>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "17px",
          lineHeight: 1.65,
          color: INK_SOFT,
          maxWidth: "640px"
        }}>
            Every photo on this page is one row in a much bigger ledger. The
            numbers below are what 17 years of Sydney garage door work
            actually adds up to.
          </p>
        </div>

        {/* Stat grid — 3x2 with featured 2x */}
        <div className="grid gap-5" style={{
        gridTemplateColumns: "repeat(6, 1fr)"
      }}>
          {STATS.map(({
          Icon,
          value,
          label,
          detail,
          featured
        }, i) => <article key={label} className="relative rounded-2xl overflow-hidden transition-all hover:translate-y-[-2px] hover:shadow-xl" style={{
          gridColumn: featured ? "span 3" : "span 2",
          backgroundColor: featured ? NAVY : "#ffffff",
          color: featured ? "#ffffff" : INK,
          border: featured ? `2px solid ${GOLD}66` : `1px solid rgba(8,20,56,0.08)`,
          boxShadow: featured ? `0 20px 50px rgba(8,20,56,0.3), 0 0 0 1px ${GOLD}33` : "0 4px 14px rgba(8,20,56,0.06)"
        }}>
              {/* Top gold edge for featured */}
              {featured && <div aria-hidden className="absolute top-0 left-0 right-0 h-1" style={{
            background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`
          }} />}

              {/* Left gold accent for non-featured */}
              {!featured && <div aria-hidden className="absolute top-0 bottom-0 left-0" style={{
            width: "3px",
            background: `linear-gradient(180deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`
          }} />}

              {/* Number stamp */}
              <div aria-hidden className="absolute pointer-events-none select-none" style={{
            top: "16px",
            right: "16px",
            fontFamily: FONT_HEAD,
            fontSize: featured ? "100px" : "70px",
            fontWeight: 800,
            color: featured ? `${GOLD}10` : `${GOLD}12`,
            lineHeight: 1,
            letterSpacing: "-0.04em",
            opacity: 0.6
          }}>
                {String(i + 1).padStart(2, "0")}
              </div>

              <div className="p-7 md:p-9 relative" style={{
            paddingLeft: featured ? undefined : "32px"
          }}>
                {/* Icon */}
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-lg mb-5" style={{
              width: featured ? 56 : 48,
              height: featured ? 56 : 48,
              backgroundColor: featured ? `${GOLD}22` : `${GOLD}15`,
              border: `1px solid ${GOLD}${featured ? "55" : "33"}`
            }}>
                  <Icon size={featured ? 26 : 22} color={featured ? GOLD : GOLD_DARK} strokeWidth={2.25} />
                </span>

                {/* Big value */}
                <div style={{
              fontFamily: FONT_HEAD,
              fontSize: featured ? "clamp(4rem, 7vw, 5.5rem)" : "clamp(2.6rem, 4vw, 3.4rem)",
              fontWeight: 900,
              lineHeight: 0.95,
              color: featured ? GOLD : NAVY,
              letterSpacing: "-0.04em",
              marginBottom: "10px"
            }}>
                  {value}
                </div>

                {/* Label */}
                <div style={{
              fontFamily: FONT_HEAD,
              fontSize: featured ? "1.1rem" : "0.95rem",
              fontWeight: 800,
              color: featured ? "#ffffff" : NAVY,
              letterSpacing: "-0.015em",
              marginBottom: "8px"
            }}>
                  {label}
                </div>

                {/* Detail */}
                <p style={{
              fontFamily: FONT_BODY,
              fontSize: featured ? "13.5px" : "12.5px",
              lineHeight: 1.55,
              color: featured ? "rgba(255,255,255,0.72)" : INK_SOFT
            }}>
                  {detail}
                </p>
              </div>
            </article>)}
        </div>

        {/* Bottom signature */}
        <div className="mt-12 flex items-center justify-center gap-3">
          <span style={{
          fontFamily: FONT_HEAD,
          fontSize: "14px",
          fontWeight: 800,
          letterSpacing: "0.22em",
          color: GOLD_DARK
        }}>
            GG
          </span>
          <span style={{
          fontFamily: FONT_BODY,
          fontSize: "12px",
          color: INK_SOFT,
          letterSpacing: "0.08em",
          textTransform: "uppercase"
        }}>
            Gold Garage Door · Bass Hill, NSW · Est. 2009
          </span>
        </div>
      </div>
    </section>;
}